<template>
    <div class="w-full flex flex-col flex-1">
        <div class="w-full h-14 rounded-t-lg bg-background-3 border border-interactive-11 flex justify-between items-center px-4 box-border">
            <span class="cursor-pointer" @click="previousStep">&lt;</span>
            <span>Payment details</span>
            <div></div>
        </div>
        <div class="w-full flex flex-col pr-4 pl-4 pb-4 flex-1">
            <div class="w-full flex flex-wrap mt-4">
                <div class="w-full flex items-center justify-center h-12 rounded-[4px] cursor-pointer border border-interactive-8 px-3 box-border">
                    <div id="card-number" class="w-full"></div>
                </div>
                <div class="w-full flex mt-3">
                    <div class="flex-1 mr-2 flex items-center justify-center h-12 rounded-[4px] cursor-pointer border border-interactive-8 px-3 box-border">
                        <div id="card-expiry" class="w-full"></div>
                    </div>
                    <div class="flex-1 ml-2 flex items-center justify-center h-12 rounded-[4px] cursor-pointer border border-interactive-8 px-3 box-border">
                        <div id="card-cvc" class="w-full"></div>
                    </div>
                </div>
                <div class="w-full">
                    {{ stripeErrors }}
                </div>
            </div>
            <div class="flex-1"></div>
            <Button label="Next step" class="mt-4" @click="generatePaymentMethod" :loading="loading" />
        </div>
    </div>
</template>

<script setup>
import Button from "@/components/Button.vue";
import { loadStripe } from '@stripe/stripe-js';
import {computed, onMounted, ref} from "vue";
import {useAPI} from "@/composables/useAPI";
import {useDonationStore} from "@/store/useDonationStore";
import {storeToRefs} from "pinia";

const {
    amountToBeDonated,
    isCustomDonation,
    billingInfo,
    userSupportFees,
    userDeclineGift,
    organizationSlug,
    selectedDonationFrequency,
    isDonationCompleted,
    error
} = storeToRefs(useDonationStore());
const {previousStep, withMedallionGift} = useDonationStore();
const {activeLevelDonationIndex} = useDonationStore();

const params = new URLSearchParams(window.location.search);
const organizationId = params.get('org') || 0;

const stripePromise = loadStripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY);

let elements;
let cardNumber;
let cardExpiry;
let cardCvc;

const stripeErrors = ref('');

const predefinedCheckoutFields = {
    h_title: '',
    phone: '',
    home_phone: '',
    pac_fop_fields: '',
    return_donor: 0, //todo: see what happens with the return donor tax
    payment_method: 'stripe',
    take_stickers: false,
    ship_with_billing_address: 'on',
    phone_number: '+123456789'
}

const props = defineProps({
    donorType: {
        type: Number,
        required: true
    },
})

const loading = ref(false);

const {processDonation} = useAPI();

async function initCreditCardFields() {
    const stripe = await stripePromise;

    elements = stripe.elements();
    const style = {
        base: {
            color: '#32325d',
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': {
                color: '#aab7c4'
            },
        },
        invalid: {
            color: '#fa755a',
            iconColor: '#fa755a'
        }
    }

    cardNumber = elements.create('cardNumber', {style: style});
    cardExpiry = elements.create('cardExpiry', {style: style});
    cardCvc = elements.create('cardCvc', {style: style});

    cardNumber.mount('#card-number');
    cardExpiry.mount('#card-expiry');
    cardCvc.mount('#card-cvc');
}

async function generatePaymentMethod() {
    if(loading.value) {
        return
    }

    const stripe = await stripePromise;

    try {
        const { paymentMethod, error } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardNumber,
            billing_details: {
                name: billingInfo.value.first_name + ' ' + billingInfo.value.last_name,
                email: billingInfo.value.email,
                address: {
                    line1: billingInfo.value.billing_address_1 + ' ' + billingInfo.value.billing_address_2,
                    city: billingInfo.value.city,
                    state: billingInfo.value.state,
                    postal_code: billingInfo.value.zip,
                    country: 'US',
                }
            }
        });

        if (error) {
            console.error('Stripe Elements Error:', error);
            loading.value = false;
            return;
        }

        handleDonation(paymentMethod)
    } catch (error) {
        console.error('Error:', error);
        loading.value = false;
    }
}

function generateTransactionId() {
    const now = new Date();
    const timestamp = now.getTime();
    const randomPart = Math.floor(Math.random() * 1000000); // Generate a random number up to 1,000,000
    return `TID_${organizationId}_${timestamp}_${randomPart}`;
}


function trackSuccessEvent(clientAnalyticsId = null) {
    const masterAnalyticsId = process.env.VUE_APP_GOOGLE_ANALYTICS_ID;

    const analyticsIdsToSendTo = clientAnalyticsId
        ? [masterAnalyticsId, clientAnalyticsId]
        : [masterAnalyticsId];

    // Trigger Google Analytics purchase event
    if(window.gtag) {
        window.gtag('event', 'purchase', {
            transaction_id: generateTransactionId(),
            value: amountToBeDonated.value,
            currency: "USD",
            items: [{
                item_id: "SKU_FOR_DONATION",
                item_name: `${organizationSlug.value} donation`,
                affiliation: "DW",
                price: amountToBeDonated.value,
                quantity: 1
            }],
            send_to: analyticsIdsToSendTo
        });
    }
}

async function handleDonation(paymentMethod) {
    loading.value = true;
    const formData = new FormData();

    Object.entries(billingInfo.value).forEach(([key, value]) => {
        formData.append(key, value);
    });

    Object.entries(predefinedCheckoutFields).forEach(([key, value]) => {
        formData.append(key, value);
    });

    formData.append('donor_type', props.donorType) //todo: handle the donor type dynamically (0 = individual, 1 = organization)
    formData.append('donation_amount', amountToBeDonated.value)

    formData.append('card_s_pm_key', paymentMethod.id)
    formData.append('cc_s_name', `${billingInfo.value.first_name} ${billingInfo.value.last_name}`)

    formData.append('stripe_tax', userSupportFees.value)

    formData.append('gift_declined', userDeclineGift.value)

    formData.append('with_medallion', withMedallionGift);

    formData.append('donation_frequency_id', selectedDonationFrequency.value.id);
    if(selectedDonationFrequency.value.is_recurring) {
        formData.append('recurring_donation', isRecurring.value);

        if(!isCustomDonation.value) {
            formData.append('active_level', activeLevelDonationIndex);
        }
    }

    formData.append('widget_donation', true)

    processDonation(organizationSlug.value, formData).then(() => {
        trackSuccessEvent();

        isDonationCompleted.value = true;
        cardNumber.clear();
        cardExpiry.clear();
        cardCvc.clear();
    }).catch((err) => {
        error.value = 'An error occured while processing your payment. Please try again later.';
        console.error(err);
    }).finally(() => {
        loading.value = false;
    })
}

const isRecurring = computed(() => {
    if(!selectedDonationFrequency.value) {
        return 'one-time';
    }
    return selectedDonationFrequency.value.is_recurring ? 'recurring' : 'one-time';
})

onMounted(() => {
    initCreditCardFields();
})
</script>